/// ObrasContainer.jsx
import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TablaObras from './TablaObras';
import Filtros from './Filtros';
import Paginacion from './Paginacion'; // Asegúrate de tener este componente

import { useLocalStorage } from "../hooks/useLocalStorage";
import {useEstadoApp} from "../context/EstadoAppContext";
import { AuthContext } from '../context/authContext';


// --- 🛠️ FUNCIONES AUXILIARES (Definidas fuera del componente) 🛠️ ---

// Datos de ejemplo para que el código sea ejecutable
const mockData = [
	{
		"id": 419,
		"cg_01_id_partido_obra": "137-001",
		"nombre_obra": "prueba exp Area de Proyectos",
		"area_responsable": "ÁREA DE PROYECTOS",
		"tipo_subsidio": "Subsidio a municipio",
		"partido_nombre": "Lezama",
		"organismo_solicitante": "",
		"organismo_otro": null,
		"monto_obra": "100000",
		"tramitacion_interna_jsonb": {
			"resolucion": true,
			"documentacion_pago_ok": true
		},
		"tramitacion_externa_jsonb": [
			{ "id": "exp-PRO-0", "exp": "EX2025-1419-PRO", "objeto": "Proyecto" },
			{ "id": "exp-REN-1", "exp": "EX2025-1549-REN", "objeto": "Rendición" },
			{ "id": "exp-ADE-2", "exp": "EX2025-0657-ADE", "objeto": "Adenda" }
		],
		"pago_proyectos_jsonb": [
			{ "monto": "20000", "tipo_pago": "anticipo", "fecha_pago": "2025-10-06T03:00:00.000Z" },
			{ "monto": "50000", "tipo_pago": "2do_pago", "fecha_pago": "2025-10-10T03:00:00.000Z" },
			{ "monto": "", "tipo_pago": "3er_pago", "fecha_pago": null },
			{ "monto": "10000", "tipo_pago": "adenda", "fecha_pago": "2025-10-22T03:00:00.000Z", "total_adenda": "10000" }
		],
		"pago_mejoras_jsonb": [],
		"pago_microcreditos_jsonb": [],
		"observ_seguimiento_obra": "Observación prueba de Proyectos"
	},
	{
		"id": 420,
		"cg_01_id_partido_obra": "137-002",
		"nombre_obra": "prueba exp Area de Mejoras",
		"area_responsable": "ÁREA DE MEJORAS",
		"tipo_subsidio": "Subsidio a ONG",
		"partido_nombre": "La Plata",
		"organismo_solicitante": "ONG Ejemplo",
		"organismo_otro": null,
		"monto_obra": "50000",
		"tramitacion_interna_jsonb": {
			"resolucion": false,
			"documentacion_pago_ok": true
		},
		"tramitacion_externa_jsonb": [
			{ "id": "exp-PRO-3", "exp": "EX2025-9999-PRO", "objeto": "Proyecto" },
			{ "id": "exp-REN-4", "exp": "EX2025-8888-REN", "objeto": "Rendición" },
		],
		"pago_proyectos_jsonb": [],
		"pago_mejoras_jsonb": [
			{ "monto": "50000", "tipo_pago": "total", "fecha_pago": "2025-11-01T03:00:00.000Z" },
			{ "monto": "", "tipo_pago": "adenda", "fecha_pago": null, "total_adenda": "" }
		],
		"pago_microcreditos_jsonb": [],
		"observ_seguimiento_obra": "Observación de Mejoras con Pago Total"
	}
];

// Función auxiliar para formatear la fecha
const formatDate = (dateString) => {
	if (!dateString) return 'N/A';
	return new Date(dateString).toLocaleDateString('es-AR');
};

// 1. Determina los pagos válidos, el total pagado (sin adenda) y el último pago.
const getPagosData = (item) => {
	// Manejo de nulidad para area_responsable
	const area = item.area_responsable || '';

	const pagosArray = area.includes('ÁREA DE PROYECTOS')
		? item.pago_proyectos_jsonb
		: area.includes('ÁREA DE MEJORAS')
			? item.pago_mejoras_jsonb
			: item.pago_microcreditos_jsonb || []; // Incluye Microcréditos si es otra área

	// Pagos Válidos (monto > 0 y fecha_pago)
	const pagosValidos = (pagosArray || []).filter(p => parseFloat(p.monto) > 0 && p.fecha_pago);
	//console.log("PAGOS VALIDOS", pagosValidos)

	// Pagos de la Obra Principal (excluye Adenda)
	const pagosObraPrincipal = pagosValidos.filter(p => p.tipo_pago !== 'adenda');

	// Monto Total Pagado (SOLO obra principal)
	const montoTotalPagadoObra = pagosObraPrincipal.reduce((sum, p) => sum + parseFloat(p.monto), 0);

	// El último pago considera *todos* los pagos (incluida la adenda)
	/*const ultimoPago = pagosValidos.length > 0
		? pagosValidos.sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago))[0]
		: null;*/
	// ⭐️ CORRECCIÓN AQUÍ: El último pago solo considera la obra principal
    const ultimoPago = pagosObraPrincipal.length > 0
        ? pagosObraPrincipal.sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago))[0]
        : null;

	return {
		montoTotalPagado: montoTotalPagadoObra,
		ultimoPago,
		pagosValidos, // Array completo de pagos válidos
		pagosObraPrincipal // Array de pagos válidos excluyendo Adenda
	};
};

// 2. Determina la etapa de pago según el porcentaje y el area responsable.
const getEtapaPago = (percentage, area) => {
	//if (area === 'ÁREA DE PROYECTOS') {
		if (percentage === 0) return 'Sin Pago';
		if (percentage > 0 && percentage <= 50) return 'Anticipo';
		if (percentage > 50 && percentage <= 99) return '2do. Pago';
		if (percentage === 100) return 'Total';
		return 'Pago Excedido';
	/*} else if(area === 'AREA DE MEJORAS' || area === 'AREA DE MICROCRÉDITOS') {
		if (percentage === 0) return 'Sin Pago';		
		if (percentage > 0) return 'Pago Total';
		return 'Pago Excedido';
	}*/
};

// --- 🏗️ COMPONENTE PRINCIPAL ---

const TablaTramitacionPagosContainer = ({arrayEditando}) => {

	const { setItem, removeItem } = useLocalStorage();
	const {user} = useContext(AuthContext)

	const { estadoAppX, cambiarEstadoApp } = useEstadoApp(); // Usamos nuestro custom hook

    const navigate = useNavigate();
	const [data, setData] = useState([]); // Aquí iría tu fetch de la API
	
	const [filters, setFilters] = useState({
		objeto: '',
		documentacion: '', //Todos
		etapaPago: '', //Todos
		// ⭐️ NUEVOS FILTROS DE FECHA ⭐️
        fechaInicio: '', 
        fechaFin: '',
		searchQuery: ''
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	
	 const [isEditingPagos, setIsEditingPagos] = useState(false);

	useEffect(() => {
		const fetchDatos = async () => {
			setIsLoading(true);
			try {
				const response = await fetch('/api/tabla_tramitacion_pagos');
				if (!response.ok) {
					throw new Error('Error al obtener los datos de la API');
				}
				const data = await response.json();
				setData(data);
			} catch (err) {
				setError(err.message);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDatos();
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, [filters]);

	const handleFilterChange = (filterName, value) => {
		setFilters(prevFilters => ({
			...prevFilters,
			[filterName]: value,
		}));
	};

	const limpiarFiltros = () => {
		setFilters({
			objeto: '',
			documentacion: '',
			etapaPago: '',
			fechaInicio: '', 
			fechaFin: '',
			searchQuery: ''
		});
	};

	// 🌟 Pre-procesamiento y aplanamiento de datos
	const processedData = useMemo(() => {
		let result = [];

		data.forEach(item => {
			const { montoTotalPagado, ultimoPago, pagosValidos } = getPagosData(item);
			const area = item.area_responsable || '';
			const observ_seguimiento_obra = item.observ_seguimiento_obra || "";
			const nroExpProyecto = item.tramitacion_externa_jsonb.find(t => t.objeto === 'Proyecto')?.exp || 'N/A';

			let organismo_solicitante = item.organismo_otro;
			if (item.tipo_subsidio === "Subsidio a municipio") {
				organismo_solicitante = "Municipio de " + item.partido_nombre;
			} else if (item.tipo_subsidio === "Subsidio a ONG") {
				organismo_solicitante = "ONG " + item.organismo_solicitante;
			}

			const tramitacionesFiltradas = item.tramitacion_externa_jsonb.filter(t => t.objeto === 'Rendición' || t.objeto === 'Adenda');

			tramitacionesFiltradas.forEach(tramite => {
				const objeto = tramite.objeto;
				let montoConvenioAdenda = '';
				let montoPagadoFila = montoTotalPagado;
				let avancePagos = 0;
				let etapaActual = '';

				if (objeto === 'Rendición') {
					montoConvenioAdenda = `$${parseFloat(item.monto_obra).toLocaleString('es-AR')}`;

					if (parseFloat(item.monto_obra) > 0) {
						avancePagos = (montoPagadoFila / parseFloat(item.monto_obra)) * 100;
						if (avancePagos > 100) avancePagos = 100;
					}
					etapaActual = getEtapaPago(avancePagos, area);//agregar el area tambien
					console.log("ETAPA ACTUAL", etapaActual)

				} else if (objeto === 'Adenda') {
					// Si es Adenda, el monto conveniado es el de la Adenda
					//let adendaMonto = 0;
					let adendaMontoConvenido = 0;
					let adendaMontoPagado = 0;

					const pagosArray = area.includes('ÁREA DE PROYECTOS') ? item.pago_proyectos_jsonb : item.pago_mejoras_jsonb;

					if (pagosArray) {
						const adendaData = pagosArray.find(p => p.tipo_pago === 'adenda');
						
						if (adendaData) {
							// ⭐️ CORRECCIÓN CLAVE 1: Monto Convenido = total_adenda ⭐️
							// Si total_adenda no existe o es vacío, usamos 'monto' como fallback si existe.
							const montoTotalAdendaStr = adendaData.total_adenda || adendaData.monto;
							adendaMontoConvenido = parseFloat(montoTotalAdendaStr) || 0;
							montoConvenioAdenda = adendaMontoConvenido > 0 ? `$${adendaMontoConvenido.toLocaleString('es-AR')}` : 'N/A';
							
							// ⭐️ CORRECCIÓN CLAVE 2: Monto Pagado = monto (del registro de adenda) ⭐️
							adendaMontoPagado = parseFloat(adendaData.monto) || 0;
							
							montoPagadoFila = adendaMontoPagado; // El monto total pagado para la fila Adenda
							
							if (adendaMontoConvenido > 0) {
								avancePagos = (adendaMontoPagado / adendaMontoConvenido) * 100;
							}
						} else {
							montoConvenioAdenda = 'N/A';
						}
					} else {
						montoConvenioAdenda = 'N/A';
					}

					// ⭐️ CORRECCIÓN CLAVE 3: Determinación de la Etapa y Avance ⭐️
					if (avancePagos >= 100 && adendaMontoConvenido > 0 && adendaMontoPagado > adendaMontoConvenido) {
						etapaActual = 'PAGO EXCEDIDO'; // Inconsistencia si se paga más
					} else if (avancePagos >= 100 && adendaMontoConvenido > 0) {
						avancePagos = 100; // Cap a 100% si está totalmente pagado
						etapaActual = 'Pagado Totalmente';
					} else if (avancePagos > 0) {
						etapaActual = 'Pago Parcial'; // Incompleto
					} else {
						etapaActual = 'Pendiente';
					}
				}

				result.push({
					id: `${item.id}-${tramite.id}`,
					nombreObra: item.nombre_obra,
					nroExpProyecto: nroExpProyecto,
					nroExpediente: tramite.exp,
					objeto: objeto,
					organismoSolicitante: organismo_solicitante,
					partidoNombre: item.partido_nombre, // ⭐️ NUEVA PROPIEDAD PARA FILTRAR ⭐️
					montoConvenioAdenda: montoConvenioAdenda,
					montoTotalPagado: `$${montoPagadoFila.toLocaleString('es-AR')}`, // Muestra solo el monto relevante a la fila
					avancePagos: avancePagos,
					etapaActual: etapaActual,
					ultimoPago: ultimoPago ? formatDate(ultimoPago.fecha_pago) : 'N/A',
					// ⭐️ NUEVA PROPIEDAD PARA FILTRAR ⭐️
					ultimoPagoISO: ultimoPago ? ultimoPago.fecha_pago : null,
					observaciones: observ_seguimiento_obra,
					// Datos para filtros
					tramitacionInterna: item.tramitacion_interna_jsonb,
					pagosValidos: pagosValidos,
					montoObraRendicion: parseFloat(item.monto_obra),
					id_partido_obra: item.cg_01_id_partido_obra
				});
			});
		});

		return result;
	}, [data]);

	// 🔄 Lógica de Filtrado
	const filteredData = useMemo(() => {
		let currentData = processedData;
		const { fechaInicio, fechaFin, searchQuery } = filters; // Desestructuramos los nuevos filtros

		// 1. Filtro por Objeto
		if (filters.objeto) {
			currentData = currentData.filter(item => item.objeto === filters.objeto);
		}

		// 2. Filtro por Documentación
		if (filters.documentacion) { //!== 'Todos'
			currentData = currentData.filter(item => {
				const { resolucion, documentacion_pago_ok } = item.tramitacionInterna;
				switch (filters.documentacion) {
					case 'Resolución':
						return resolucion === true;
					case 'Documentación para el pago':
						return documentacion_pago_ok === true;
					case 'Ambas':
						return resolucion === true && documentacion_pago_ok === true;
					default:
						return true;
				}
			});
		}

		// 3. Filtro por Etapa de pago (solo Rendición)
		if (filters.etapaPago) {//!== 'Todos'
			currentData = currentData.filter(item => {
				if (item.objeto !== 'Rendición') return false;

				const totalPagado = parseFloat(item.montoTotalPagado.replace('$', '').replace(/\./g, '').replace(',', '.'));
				const montoObra = item.montoObraRendicion;
				if (montoObra === 0) return false;

				const porcentajePagado = (totalPagado / montoObra) * 100;

				switch (filters.etapaPago) {
					case 'anticipo':
						return item.pagosValidos.some(p => p.tipo_pago === 'anticipo') ;//&& porcentajePagado > 0 && porcentajePagado <= 50
					case '2do_pago':
						return item.pagosValidos.some(p => p.tipo_pago === '2do_pago'); // && porcentajePagado > 50 && porcentajePagado <= 99
					case '3er_pago':
						return item.pagosValidos.some(p => p.tipo_pago === '3er_pago'); //&& porcentajePagado > 80 && porcentajePagado < 100
					case 'total':
						return item.pagosValidos.some(p => p.tipo_pago === 'total' );//|| porcentajePagado === 100
					default:
						return false;
				}
			});
		}

		// 4. Filtro por Rango de Fechas (Ultimo Pago)
		if (fechaInicio || fechaFin) {
			currentData = currentData.filter(item => {
				const pagoDate = item.ultimoPagoISO ? new Date(item.ultimoPagoISO) : null;
				
				// Si no hay pago, no se incluye en el filtro de fechas.
				if (!pagoDate) return false;
	
				let cumpleInicio = true;
				let cumpleFin = true;
				
				// Compara con Fecha de Inicio
				if (fechaInicio) {
					// Aseguramos que la fecha de inicio es el comienzo del día
					const start = new Date(fechaInicio);
					start.setHours(0, 0, 0, 0); 
					cumpleInicio = pagoDate >= start;
				}
	
				// Compara con Fecha de Fin
				if (fechaFin) {
					// Aseguramos que la fecha de fin es el final del día
					const end = new Date(fechaFin);
					end.setHours(23, 59, 59, 999); 
					cumpleFin = pagoDate <= end;
				}
	
				return cumpleInicio && cumpleFin;
			});
		}

		// ⭐️ 5. Filtro por Búsqueda de Texto (Search Query) ⭐️
		if (searchQuery) {
			const query = searchQuery.toLowerCase().trim();
			
			currentData = currentData.filter(item => {
				// Campos a buscar: Nombre de obra, Nro. Exp. Proyecto, nombre de Partido.
				const idPartido = item.id_partido_obra?.toString().toLowerCase() || '';
				const nombreObra = item.nombreObra?.toLowerCase() || '';
				const nroExpProyecto = item.nroExpProyecto?.toLowerCase() || '';
				// Necesitamos acceder al nombre del partido original. 
				// Para esto, debemos asegurarnos de agregarlo en processedData.
				
				// Si no lo tienes aún en processedData, asegúrate de agregarlo en el paso A:
				// "partidoNombre: item.partido_nombre,"
				const partidoNombre = item.partidoNombre?.toLowerCase() || ''; 
				
				return (
					idPartido.includes(query) ||
					nombreObra.includes(query) ||
					nroExpProyecto.includes(query) ||
					partidoNombre.includes(query)
				);
			});
		}

		return currentData;
	}, [processedData, filters]);

	// ⬇️ Lógica de Paginación ⬇️
	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
	const totalPages = Math.ceil(filteredData.length / itemsPerPage);

	//const paginate = (pageNumber) => setCurrentPage(pageNumber);

	/*const handleIsEditing = useCallback((newIsEditing) => {
		setIsEditingPagos(newIsEditing);
	}, []); 

	useEffect(() => {
		console.log("IS EDITING PAGOS", isEditingPagos)
		//cuando sea true guardar en localStorage
		if (isEditingPagos) {
			setItem("isEditingPagos", true)
		}else{
			setItem("isEditingPagos", false)
		}
	}, [isEditingPagos]);
	*/

	if (isLoading) return <div>Cargando...</div>;
    if (error) return <div>Error: {error}</div>;
	

	return (
		<div className="w-full p-4">
			<h2 className="text-2xl font-bold mb-4">Tramitación Pagos</h2>
			{/*<div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6">*/}

				<Filtros 
					filters={filters}					
					onFilterChange={handleFilterChange}
					limpiarFiltros={limpiarFiltros}
				/>
				{/*<hr className="my-6 border-gray-200 dark:border-gray-700" />*/}

				<TablaObras 
					data={currentItems}	
					arrayEditando={arrayEditando}			
					//handleIsEditing = {handleIsEditing}
				/>

				<Paginacion
					currentPage={currentPage}
					totalPages={totalPages}
					setCurrentPage={setCurrentPage}					
					itemsPerPage={itemsPerPage}		
					setItemsPerPage={setItemsPerPage}
					totalItems={filteredData.length}
				/>
			{/*</div>*/}
		</div>
	);
};

export default TablaTramitacionPagosContainer;